import type { Theme } from '@mui/material'
import { Alert, Box, Button, LinearProgress, Stack, Typography } from '@mui/material'
import { useMutation } from '@tanstack/react-query'
import CameraControls from 'camera-controls'
import { ApplicationTypes } from 'element-domain'
import { useCallback, useEffect, useState } from 'react'
import { PerspectiveCamera } from 'three'
import { v4 } from 'uuid'

import { useLogger, useMountGuard } from '@salsita-ai/platform-project-sdk/react'
import { Dialog, PhxIcon } from '@salsita-ai/platform-project-sdk/ui'

import { SCENE_BASE_LAYER, SCENE_TABLETOP_LAYER } from '../../../scene/AppEnvironment'
import { commitPopOrNavigateToSummary } from '../../../systems/commit-pop-or-summary'
import { configuratorInterface } from '../../../systems/configurator-interface'
import { FinalizeCartSuccessDialogContent } from './dialog-content/FinalizeCartSuccessDialogContent'

const IMAGE_HEIGHT = 720
const IMAGE_WIDTH = 1080

export default function FinalizeCartDialog() {
  const [finalizationUuid, setFinalizationUuid] = useState<string | null>(null)

  const frontendData = configuratorInterface.useFrontendData()

  const { navigationSystem, storeSystem, configurationId } = configuratorInterface.useDomain()
  const { imageRenderingSystem } = configuratorInterface.useConfigurator()
  const committedState = configuratorInterface.useCommittedState()
  const { configurationPersistSystem } = configuratorInterface.useDomainApplication()
  const apiClient = configuratorInterface.useAuthorizedClient()
  const isMounted = useMountGuard()
  const logger = useLogger()

  const generateImages = useCallback(
    async (finalizationUuid: string) => {
      await imageRenderingSystem.generateImages(finalizationUuid, [
        {
          name: 'clientGeneratedImage',
          width: IMAGE_WIDTH,
          height: IMAGE_HEIGHT,
          getCamera: async scene => {
            // Scene uses mm-scale units; far=10k clips easily after fit + dolly (blank export).
            const camera = new PerspectiveCamera(45, IMAGE_WIDTH / IMAGE_HEIGHT, 10, 250000)
            camera.layers.enable(SCENE_TABLETOP_LAYER)
            camera.layers.enable(SCENE_BASE_LAYER)
            const controls = new CameraControls(camera)

            const mesh = scene.getObjectByName(String(committedState.rootEntityId))

            if (mesh == null) {
              throw new Error('Root entity not found!')
            }

            await controls.fitToSphere(mesh, false)
            await controls.rotateTo(-0.5, 1.25, false)

            // Move camera farther away from the object
            await controls.dollyTo(controls.distance + 1000, false) // Increase distance by 1000 units

            // To mock the "animations"
            controls.update(1000000)

            return camera
          },
        },
      ])
    },
    [imageRenderingSystem, committedState.rootEntityId]
  )
  const finalize = useCallback(async () => {
    const finalizationUuid = v4()
    let failureStage: 'configuration_preparation' | 'finalization_request' = 'configuration_preparation'

    try {
      await Promise.all([configurationPersistSystem.save(), generateImages(finalizationUuid)]) // TODO: what is configurationPersistSystem

      failureStage = 'finalization_request'
      await apiClient.finalize(
        configurationId,
        finalizationUuid,
        {
          emailAddress: '',
          firstName: '',
          lastName: '',
          phoneNumber: '',
          message: '',
          submissionDate: new Date().toISOString(),
          addToCart: true,
        },
        undefined,
        frontendData
      )

      if (isMounted()) {
        setFinalizationUuid(finalizationUuid)
      }
    } catch (error) {
      const errorObject = error instanceof Error ? error : new Error('Unknown error')
      const productCreated = failureStage === 'configuration_preparation' ? false : 'unknown'

      window.dataLayer = window.dataLayer ?? []
      window.dataLayer.push({
        event: 'configurator_add_to_cart_failed',
        configurationId,
        finalizationUuid,
        failureStage,
        productCreated,
        errorName: errorObject.name,
        errorMessage: errorObject.message,
        quantity: 1,
        result: 'failure',
        source: 'salsita-configurator',
        timestamp: new Date().toISOString(),
      })

      throw errorObject
    }
  }, [apiClient, configurationPersistSystem, generateImages, configurationId, isMounted, frontendData])

  const handleBack = useCallback(() => {
    commitPopOrNavigateToSummary(navigationSystem, storeSystem, ApplicationTypes.NavigationState.Summary)
  }, [navigationSystem, storeSystem])

  const { mutate, isError, isSuccess } = useMutation({
    mutationKey: ['finalize'],
    mutationFn: finalize,
    onError(error) {
      logger.error('Finalization failed:', {}, error)
    },
  })

  useEffect(() => {
    mutate()
  }, [mutate])

  return (
    <>
      {isSuccess && finalizationUuid && (
        <FinalizeCartSuccessDialogContent finalizationUuid={finalizationUuid} onClose={handleBack} />
      )}
      {!isSuccess && (
        <Dialog.ContentLayout
          controlsPosition="end"
          header={
            <Box
              component="div"
              sx={(theme: Theme) => ({
                backgroundColor: theme.palette.background.default,
                blockSize: 180,
                color: theme.palette.success.main,
                display: 'grid',
                placeItems: 'center',
              })}
            >
              <PhxIcon name="CheckCircle" style={{ fontSize: 80 }} />
            </Box>
          }
          body={
            <Stack sx={{ gap: 2, px: 1 }}>
              <Typography>Creating new Cart Item...</Typography>
              <LinearProgress />
              {isError && (
                <Alert severity="error">
                  <b>Adding to Cart failed.</b> Please try again later. <br />
                  If the issue persists, contact website support.
                </Alert>
              )}
            </Stack>
          }
          controls={
            <Button variant="text" onClick={handleBack} startIcon={<PhxIcon name="ArrowLeft" />}>
              Back to Summary
            </Button>
          }
        />
      )}
    </>
  )
}
